import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
// Do not import motion/react here: it pulls ~120 KiB of animation runtime into
// the entry chunk. Reduced motion is applied per subtree by MotionScope.
import App from './App.tsx'
import './index.css'
import ErrorBoundary from './components/ErrorBoundary'
import React from 'react'
import { initAnalytics } from './lib/analytics/posthog'
import { rememberLanding } from './lib/personas/intent'
import AttributionCapture from './components/outreach/AttributionCapture'

initAnalytics()
rememberLanding()

// Register the service worker only in production builds; the dev server's
// HMR responses must never be cached.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // Registration failure is non-fatal; the app still works online-only.
    });
  });
}

// Override console.warn to filter out known harmless warnings in production
if (import.meta.env.PROD) {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    const message = args.join(' ');
    
    // Filter out known harmless warnings
    if (message.includes('Permissions-Policy') ||
        message.includes('interest-cohort') ||
        message.includes('browsing-topics') ||
        message.includes('private-state-token') ||
        message.includes('run-ad-auction') ||
        message.includes('attribution-reporting') ||
        message.includes('join-ad-interest-group')) {
      return; // Silently ignore these warnings
    }
    
    // Pass through other warnings
    originalWarn.apply(console, args);
  };
}

// Create a client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (was cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
      refetchOnWindowFocus: false, // Disable refetch on window focus for better performance
      refetchOnReconnect: true, // Refetch when connection is restored
    },
    mutations: {
      retry: 1, // Retry mutations once
      retryDelay: 1000, // 1 second delay before retry
    },
  },
})

// Dynamically import Analytics only in production
const AnalyticsComponent = process.env.NODE_ENV === 'production' 
  ? React.lazy(() => import('@vercel/analytics/react').then(mod => ({ 
      default: () => <mod.Analytics 
        beforeSend={(event) => process.env.NODE_ENV === 'production' ? event : null} 
      /> 
    })))
  : () => null;

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <BrowserRouter>
      <AttributionCapture />
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <App />
          <React.Suspense fallback={null}>
            <AnalyticsComponent />
          </React.Suspense>
        </HelmetProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </ErrorBoundary>
);
